import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { AppError } from '@obraplan/core/application/errors/app-error';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, PasswordInput } from '@/components/ui/input';

const formSchema = z
  .object({
    // O mesmo mínimo do `setPasswordSchema` do núcleo. A tela avisa antes de enviar; quem
    // recusa continua sendo o caso de uso (RN-134).
    password: z.string().min(10, 'A senha precisa de ao menos 10 caracteres.').max(200),
    confirmacao: z.string(),
  })
  .refine((valores) => valores.password === valores.confirmacao, {
    message: 'As duas senhas não são iguais.',
    path: ['confirmacao'],
  });

type FormValues = z.infer<typeof formSchema>;

/**
 * Dar senha a alguém — o caminho de entrada de um sistema fechado.
 *
 * Aqui não há autocadastro nem "esqueci minha senha": quem entra é quem foi cadastrado, e
 * a primeira senha vem de quem administra. É a razão de esta tela existir — sem ela, um
 * perfil recém-criado nasceria sem porta.
 *
 * **A confirmação não é burocracia.** A pessoa que digita não é a que vai usar a senha:
 * um erro de digitação aqui só apareceria do outro lado, com alguém sem conseguir entrar e
 * sem saber por quê.
 *
 * A senha não volta em lugar nenhum — nem aqui, nem na auditoria, que registra apenas que
 * ela mudou. Combinar a senha com a pessoa é conversa fora do sistema.
 */
export function SetPasswordDialog({
  open,
  onOpenChange,
  userName,
  onSubmit,
  isPending,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  onSubmit: (password: string) => void;
  isPending: boolean;
  error: AppError | undefined;
}) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: '', confirmacao: '' },
  });

  const { reset } = form;

  // Senha digitada não sobrevive ao fechar o diálogo: reabrir para outra pessoa não pode
  // trazer o campo preenchido com a senha da anterior.
  useEffect(() => {
    reset({ password: '', confirmacao: '' });
  }, [open, reset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Definir senha de {userName}</DialogTitle>
          <DialogDescription>
            É com ela que a pessoa entra. Combine-a com quem vai usá-la — o sistema não a mostra de
            volta em lugar nenhum.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit((valores) => onSubmit(valores.password))(event);
          }}
        >
          <Field
            label="Senha"
            htmlFor="set-password"
            required
            hint="Ao menos 10 caracteres."
            error={form.formState.errors.password?.message}
          >
            <PasswordInput
              id="set-password"
              autoComplete="new-password"
              autoFocus
              aria-invalid={form.formState.errors.password !== undefined}
              {...form.register('password')}
            />
          </Field>

          <Field
            label="Repita a senha"
            htmlFor="set-password-confirm"
            required
            error={form.formState.errors.confirmacao?.message}
          >
            <PasswordInput
              id="set-password-confirm"
              autoComplete="new-password"
              aria-invalid={form.formState.errors.confirmacao !== undefined}
              {...form.register('confirmacao')}
            />
          </Field>

          {error !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {error.message}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isPending}>
              {isPending ? 'Salvando…' : 'Definir senha'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
